import { useEffect, useId, useRef, useState } from 'react'
import { isStandalone } from '../lib/standalone'
import type { Prices } from '../hooks/usePrices'
import type { AuthActions, AuthFailure } from '../hooks/useAuth'
import { Landing } from './Landing'
import { LANDING_COPY, landingLangOf, type LandingLang } from '../lib/landingCopy'
import { Wordmark } from './Wordmark'
import { WaitlistForm } from './WaitlistForm'
import { useWaitlist } from '../hooks/useWaitlist'
import { startSiteAnalytics, trackEvent } from '../lib/analytics'
import { PRELAUNCH, signedOutView } from '../lib/comingSoon'
import { useAutoFocus } from '../hooks/useAutoFocus'
import { checkEmail } from '../lib/emailCheck'

interface Props {
  auth: AuthActions
  prices: Prices
  /** The watch's QR (/?pair) brought them here — sign-in must stay open. */
  pairIntent?: boolean
  /** Just Sign in: no landing, no Create account, no waitlist link (the private /insights page). */
  formOnly?: boolean
}

type Tab = 'signin' | 'signup'

/**
 * Where the signed-out flow is. Every mail Matlist sends carries an 8-digit
 * code typed in here — never a link, which on iOS would open Safari instead of
 * the installed app. 'verify' confirms a new account (and rescues one that
 * never got confirmed); 'forgot' asks for the address, 'reset' takes the code
 * and the new password together.
 */
type Step = 'form' | 'verify' | 'forgot' | 'reset'

/**
 * supabase/config.toml's [auth.email] max_frequency: one mail of each kind per
 * address every five minutes. The server enforces it; this only keeps the
 * button honest. The code itself is valid for 15 minutes (otp_expiry).
 */
export const RESEND_COOLDOWN_S = 300
const CODE_LENGTH = 8
const MIN_PASSWORD = 6
/** What the strong password Safari suggests must hold; ours asks only the length. */
const PASSWORD_RULES = `minlength: ${MIN_PASSWORD * 2}; required: lower; required: upper; required: digit;`

export function LoginPage({ auth, prices, pairIntent = false, formOnly = false }: Props) {
  // A shared trip card's link (lib/tripCard.ts signupUrl) asks for Create account.
  // Before launch there is no Create account (lib/comingSoon.ts): the link opens Sign in.
  const [signupLink] = useState(() => !PRELAUNCH && !formOnly && new URLSearchParams(window.location.search).has('signup'))
  const [signinLink] = useState(() => new URLSearchParams(window.location.search).has('signin'))
  const [tab, setTab] = useState<Tab>(signupLink ? 'signup' : 'signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [step, setStep] = useState<Step>('form')
  const [code, setCode] = useState('')
  const [cooldown, setCooldown] = useState(0)
  // "anna@gmial.com": offered back corrected while signing up (lib/emailCheck.ts).
  const checked = checkEmail(email)
  const typo = checked.ok ? checked.suggestion : undefined
  // Someone who already installed the app doesn't need the sales pitch, so in
  // standalone it's just the form. Read once on mount — display mode doesn't
  // change within a session.
  const [standalone] = useState(isStandalone)
  const [view] = useState(() => signedOutView({ standalone: standalone || formOnly, pairIntent, signinLink }))
  const landing = view !== 'form'
  const lang: LandingLang = landingLangOf(window.location.pathname)
  // Website analytics (lib/analytics.ts) count the landing page only.
  useEffect(() => (landing ? startSiteAnalytics({ lang }) : undefined), [landing, lang])
  const formRef = useRef<HTMLDivElement>(null)
  // …and the form sits at the foot of the landing page: bring it into view.
  useEffect(() => {
    if (signupLink) formRef.current?.scrollIntoView?.({ block: 'start' })
  }, [signupLink])

  useEffect(() => {
    if (cooldown <= 0) return
    const t = setTimeout(() => setCooldown(c => c - 1), 1000)
    return () => clearTimeout(t)
  }, [cooldown])

  /** Runs one auth call with the spinner and error line; true when it succeeded. */
  async function run(call: () => Promise<AuthFailure | null>): Promise<boolean> {
    setLoading(true)
    setError(null)
    setNotice(null)
    const failure = await call()
    setLoading(false)
    if (failure) setError(describe(failure))
    return !failure
  }

  function goTo(next: Step) {
    setStep(next)
    setCode('')
    setError(null)
    setNotice(null)
  }

  async function submit() {
    if (!email.trim() || !password.trim()) return
    const address = email.trim()
    if (tab === 'signup') {
      // A typo or a one-time inbox is caught here, before an account is made
      // on it (048_email_domains.sql refuses the same domains server-side).
      const checked = checkEmail(address)
      if (!checked.ok) { setError(checked.error); return }
      if (password.length < MIN_PASSWORD) { setError(`Choose a password of at least ${MIN_PASSWORD} characters.`); return }
      setLoading(true)
      setError(null)
      const failure = await auth.signUp(address, password)
      setLoading(false)
      if (!failure) {
        goTo('verify')
        setCooldown(RESEND_COOLDOWN_S)
      } else if (rateLimited(failure)) {
        // Signed up again within the window: the first code still works.
        goTo('verify')
        setNotice(SENT_RECENTLY)
      } else {
        setError(describe(failure))
      }
      return
    }
    setLoading(true)
    setError(null)
    const failure = await auth.signIn(address, password)
    setLoading(false)
    if (!failure) return
    if (failure.code === 'email_not_confirmed') {
      // An account whose confirmation never arrived (or expired): send a fresh
      // code and go straight to entering it, instead of a dead-end error.
      goTo('verify')
      const resent = await auth.resendSignup(address)
      if (!resent) {
        setCooldown(RESEND_COOLDOWN_S)
        setNotice('This account isn’t confirmed yet. We’ve sent you a new code.')
      } else if (rateLimited(resent)) {
        setNotice('This account isn’t confirmed yet. ' + SENT_RECENTLY)
      } else {
        setError(describe(resent))
      }
      return
    }
    setError(describe(failure))
  }

  async function verify(value = code) {
    if (value.length !== CODE_LENGTH) return
    // Success signs in; App swaps this page out on its own.
    await run(() => auth.verifySignup(email.trim(), value))
  }

  async function resend() {
    const address = email.trim()
    const ok = await run(() => step === 'verify' ? auth.resendSignup(address) : auth.sendPasswordReset(address))
    if (ok) {
      setCooldown(RESEND_COOLDOWN_S)
      setNotice('New code sent.')
    }
  }

  async function sendReset() {
    if (!email.trim()) return
    setLoading(true)
    setError(null)
    const failure = await auth.sendPasswordReset(email.trim())
    setLoading(false)
    if (!failure) {
      goTo('reset')
      setCooldown(RESEND_COOLDOWN_S)
    } else if (rateLimited(failure)) {
      // Asked again within the window: the first code still works.
      goTo('reset')
      setNotice(SENT_RECENTLY)
    } else {
      setError(describe(failure))
    }
  }

  async function reset() {
    if (code.length !== CODE_LENGTH) return
    if (password.length < MIN_PASSWORD) {
      setError(`Choose a password of at least ${MIN_PASSWORD} characters.`)
      return
    }
    await run(() => auth.resetPassword(email.trim(), code, password))
  }

  /** The landing page's calls to action: pick the mode, then bring the form into view. */
  function goToForm(t: Tab) {
    setTab(t)
    setError(null)
    // Guarded — jsdom has no scrollIntoView.
    formRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }

  if (step === 'verify' || step === 'reset') {
    const resetting = step === 'reset'
    return (
      <Centered>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>
          {resetting ? 'Choose a new password' : 'Check your email'}
        </h2>
        <p style={{ color: 'var(--ml-text-secondary)', fontSize: 15, lineHeight: 1.5, textAlign: 'center', marginBottom: 24 }}>
          We sent an {CODE_LENGTH}-digit code to <strong>{email.trim()}</strong>.
          {resetting ? ' Enter it with your new password.' : ' Enter it to activate your account.'}
        </p>
        <form method="post" action="#" noValidate onSubmit={e => { e.preventDefault(); void (resetting ? reset() : verify()) }}>
        <Card>
          <Field
            label="Code" value={code} placeholder="12345678" type="text" border={resetting}
            inputMode="numeric" autoComplete="one-time-code" maxLength={CODE_LENGTH} autoFocus
            onChange={v => {
              const digits = v.replace(/\D/g, '').slice(0, CODE_LENGTH)
              setCode(digits)
              // Pasted or autofilled whole: no need to also press Confirm.
              if (!resetting && digits.length === CODE_LENGTH && !loading) void verify(digits)
            }}
          />
          {resetting && (
            <>
              {/* Which account the new password belongs to, for the password manager. */}
              <input type="email" name="email" autoComplete="username" value={email.trim()} readOnly hidden />
              <Field
                label="New password" name="password" value={password} onChange={setPassword}
                placeholder={`Min. ${MIN_PASSWORD} characters`} type="password"
                autoComplete="new-password" passwordRules={PASSWORD_RULES}
              />
            </>
          )}
        </Card>
        <Messages error={error} notice={notice} />
        <PrimaryButton type="submit"
          disabled={loading || code.length !== CODE_LENGTH || (resetting && !password)}
        >
          {loading ? '…' : resetting ? 'Set new password' : 'Confirm'}
        </PrimaryButton>
        </form>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
          <LinkButton onClick={() => void resend()} disabled={loading || cooldown > 0}>
            {cooldown > 0 ? `Resend code (${countdown(cooldown)})` : 'Resend code'}
          </LinkButton>
          <LinkButton onClick={() => { goTo('form'); setPassword('') }}>
            Use a different email
          </LinkButton>
        </div>
      </Centered>
    )
  }

  if (step === 'forgot') {
    return (
      <Centered>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>Forgot your password?</h2>
        <p style={{ color: 'var(--ml-text-secondary)', fontSize: 15, lineHeight: 1.5, textAlign: 'center', marginBottom: 24 }}>
          We&rsquo;ll email you a code to choose a new one.
        </p>
        <Card>
          <Field
            label="Email" name="email" value={email} onChange={setEmail} placeholder="you@example.com"
            type="email" autoComplete="username" autoFocus onEnter={() => void sendReset()}
          />
        </Card>
        <Messages error={error} notice={notice} />
        <PrimaryButton onClick={() => void sendReset()} disabled={loading || !email.trim()}>
          {loading ? '…' : 'Send code'}
        </PrimaryButton>
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <LinkButton onClick={() => { goTo('form'); setTab('signin') }}>Back</LinkButton>
        </div>
      </Centered>
    )
  }

  const form = (
    <>
      {!PRELAUNCH && !formOnly && <div className="ml-segmented" style={{ marginBottom: 24 }}>
        {(['signin', 'signup'] as const).map(t => (
          <button key={t} type="button" aria-pressed={tab === t} onClick={() => { setTab(t); setError(null) }}
            className={`ml-segmented__opt${tab === t ? ' is-selected' : ''}`}>
            {t === 'signin' ? 'Sign in' : 'Create account'}
          </button>
        ))}
      </div>}

      {/* A real form, its fields named: what iCloud Keychain and Google Password
          Manager look for to suggest a strong password on sign-up, fill one on
          sign-in, and offer to save it when the form is sent. */}
      <form method="post" action="#" noValidate onSubmit={e => { e.preventDefault(); void submit() }}>
      <Card>
        <Field
          label="Email" name="email" value={email} onChange={v => { setEmail(v); setError(null) }}
          placeholder="you@example.com" type="email"
          // Sign-up offers the addresses on the phone's own card; sign-in the saved accounts.
          autoComplete={tab === 'signup' ? 'email' : 'username'}
          // On the landing page the form sits below the fold; focusing it on
          // load would scroll the visitor straight past the pitch.
          autoFocus={!landing} border
        />
        <Field
          label="Password" name="password" value={password} onChange={setPassword}
          placeholder={tab === 'signup' ? `Min. ${MIN_PASSWORD} characters` : '••••••••'}
          type="password" revealable
          autoComplete={tab === 'signup' ? 'new-password' : 'current-password'}
          passwordRules={tab === 'signup' ? PASSWORD_RULES : undefined}
        />
      </Card>

      {tab === 'signup' && typo && (
        <p role="status" style={{ fontSize: 14, textAlign: 'center', margin: '0 0 12px', color: 'var(--ml-text-secondary)' }}>
          Did you mean{' '}
          <button type="button" onClick={() => setEmail(typo)}
            style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'var(--ml-text)', textDecoration: 'underline', cursor: 'pointer' }}>
            {typo}
          </button>?
        </p>
      )}

      <Messages error={error} notice={notice} />

      {tab === 'signup' && (
        <p style={{ color: 'var(--ml-text-secondary)', fontSize: 13, lineHeight: 1.5, margin: '4px 0 12px' }}>
          By creating an account you agree to the <a href="/terms.html" style={{ color: 'inherit' }}>Terms</a> and
          the <a href="/privacy.html" rel="nofollow" style={{ color: 'inherit' }}>Privacy policy</a>.
        </p>
      )}

      <PrimaryButton type="submit" disabled={loading || !email.trim() || !password.trim()}>
        {loading ? '…' : tab === 'signin' ? 'Sign in' : 'Create account'}
      </PrimaryButton>
      </form>

      {tab === 'signin' && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <LinkButton onClick={() => goTo('forgot')}>Forgot password?</LinkButton>
        </div>
      )}
    </>
  )

  if (view === 'waitlist') {
    return (
      <Landing
        prices={prices}
        lang={lang}
        mode="waitlist"
        onGetStarted={() => formRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })}
        onSignIn={() => window.location.assign('/?signin')}
        form={<div ref={formRef} id="waitlist" style={{ scrollMarginTop: 24 }}><Waitlist lang={lang} /></div>}
      />
    )
  }

  if (landing) {
    return (
      <Landing
        prices={prices}
        lang={lang}
        onGetStarted={() => goToForm('signup')}
        onSignIn={() => goToForm('signin')}
        form={<div ref={formRef} id="auth" style={{ scrollMarginTop: 24 }}>{form}</div>}
      />
    )
  }

  return (
    <Centered>
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <h1>
          <Wordmark size={46} />
        </h1>
        <p style={{ color: 'var(--ml-text-secondary)', fontSize: 15, marginTop: 6 }}>
          One list, the whole family.
        </p>
      </div>
      {form}
      {PRELAUNCH && !formOnly && (
        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14 }}>
          <a href={lang === 'sv' ? '/sv' : '/'} style={{ color: 'var(--ml-text-secondary)' }}>
            {LANDING_COPY[lang].waitlist.notMember}
          </a>
        </p>
      )}
    </Centered>
  )
}

/** The waitlist form, joined through join_waitlist(), counted as a signup in the website analytics. */
function Waitlist({ lang }: { lang: LandingLang }) {
  const { state, join } = useWaitlist(lang)
  useEffect(() => { if (state === 'done') trackEvent({ kind: 'waitlist' }) }, [state])
  return <WaitlistForm lang={lang} state={state} onJoin={join} />
}

/** The installed app's framing: one 390px column in the middle of the screen. */
function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--ml-bg)', padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: 390 }}>{children}</div>
    </div>
  )
}

function Field({ label, name, value, onChange, placeholder, type, border, autoFocus, onEnter, inputMode, autoComplete, maxLength, passwordRules, revealable }: {
  label: string; value: string; onChange: (v: string) => void
  placeholder: string; type: string; border?: boolean; autoFocus?: boolean
  /** The field's name, for the password manager (email, password). */
  name?: string
  /** Enter runs this; without it, inside a form, Enter sends the form, else it closes the keyboard. */
  onEnter?: () => void
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  autoComplete?: string; maxLength?: number
  /** Safari's rules for the strong password it suggests (passwordrules). */
  passwordRules?: string
  /** A Show / Hide toggle beside a password. */
  revealable?: boolean
}) {
  const focusRef = useAutoFocus<HTMLInputElement>(autoFocus)
  const id = useId()
  const [shown, setShown] = useState(false)
  return (
    <div style={{
      display: 'flex', alignItems: 'center', padding: '12px 16px',
      borderBottom: border ? '0.5px solid var(--ml-separator)' : 'none',
    }}>
      <label htmlFor={id} style={{ fontSize: 15, color: 'var(--ml-text-secondary)', minWidth: 100, paddingRight: 12, flexShrink: 0 }}>
        {label}
      </label>
      <input
        id={id} name={name}
        value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} type={revealable && shown ? 'text' : type} ref={focusRef}
        inputMode={inputMode} autoComplete={autoComplete} maxLength={maxLength}
        autoCapitalize={type === 'email' || type === 'password' ? 'none' : undefined}
        spellCheck={type === 'email' || type === 'password' ? false : undefined}
        // React passes an unknown lower-case attribute through as written.
        {...(passwordRules ? { passwordrules: passwordRules } : {})}
        onKeyDown={e => {
          if (e.key !== 'Enter') return
          if (onEnter) { e.preventDefault(); onEnter() } else if (!e.currentTarget.form) { e.currentTarget.blur() }
        }}
        style={{
          flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent',
          fontSize: 15, color: 'var(--ml-text)',
        }}
      />
      {revealable && (
        <button type="button" onClick={() => setShown(v => !v)} aria-pressed={shown}
          aria-label={shown ? 'Hide password' : 'Show password'}
          style={{ border: 'none', background: 'none', padding: '0 0 0 8px', fontSize: 14, color: 'var(--ml-text-secondary)', cursor: 'pointer' }}>
          {shown ? 'Hide' : 'Show'}
        </button>
      )}
    </div>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--ml-surface)', borderRadius: 'var(--ml-radius-input)',
      overflow: 'hidden', marginBottom: 16,
    }}>
      {children}
    </div>
  )
}

function Messages({ error, notice }: { error: string | null; notice: string | null }) {
  if (!error && !notice) return null
  return (
    <p role={error ? 'alert' : 'status'} style={{
      color: error ? 'var(--ml-destructive)' : 'var(--ml-text-secondary)',
      fontSize: 13, marginBottom: 12, textAlign: 'center',
    }}>
      {error ?? notice}
    </p>
  )
}

function PrimaryButton({ onClick, disabled, children, type = 'button' }: {
  onClick?: () => void; disabled: boolean; children: React.ReactNode
  /** 'submit' inside a form: the form's onSubmit runs it (and the password manager sees it sent). */
  type?: 'button' | 'submit'
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%', padding: '15px', border: 'none', borderRadius: 'var(--ml-radius-input)',
        background: 'var(--ml-primary-bg)', color: 'var(--ml-primary-fg)', fontSize: 16, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
                transition: 'opacity 0.15s',
      }}
    >
      {children}
    </button>
  )
}

function LinkButton({ onClick, disabled, children }: {
  onClick: () => void; disabled?: boolean; children: React.ReactNode
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{
      border: 'none', background: 'none', padding: 4, fontSize: 14,
      color: 'var(--ml-text-secondary)',
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
    }}>
      {children}
    </button>
  )
}

/** GoTrue's wording, except where it reads like a fault in the app. */
function describe(failure: AuthFailure): string {
  switch (failure.code) {
    case 'otp_expired':
      return 'That code is wrong or has expired. Check it, or send a new one.'
    case 'over_email_send_rate_limit':
      return 'A new code can be sent once every five minutes. The last one still works for 15 minutes.'
    case 'invalid_credentials':
      return 'Wrong email or password.'
    default:
      return failure.message
  }
}

const SENT_RECENTLY = 'We emailed you a code a few minutes ago \u2014 it still works for 15 minutes.'

/** The server's one-mail-per-window rule (max_frequency), not a real failure. */
function rateLimited(failure: AuthFailure): boolean {
  return failure.code === 'over_email_send_rate_limit'
}

/** 300 → "5:00", 59 → "0:59". */
function countdown(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}
